import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from 'services'
import { IdParams } from 'types/api'
import { IPaginationQueryInfo } from 'types/pagination'
import { IContractItem, IContractsResponse } from 'types/contracts'
import { TContractsSearchFilters } from 'pages/purchase-pages/concluded-contracts'

type IContractsPublicQueryInfo = IPaginationQueryInfo & TContractsSearchFilters

export const contractsApi = createApi({
  reducerPath: 'contractsApi',
  baseQuery,
  tagTypes: [
    'Contracts',
    'Contract',
    'ContractFiles',
    'ContractFile',
    'ContractsPublic',
  ],
  endpoints: (builder) => ({
    contracts: builder.query<IContractsResponse, IPaginationQueryInfo>({
      query: (params) => {
        return {
          url: '/contracts',
          method: 'get',
          params,
        }
      },
      providesTags: ['Contracts'],
    }),
    contractsPublic: builder.query<IContractsResponse, IContractsPublicQueryInfo>({
      query: ({
        page,
        size,
        contractorOrganizationName,
        lotName,
        createdDateFrom,
        createdDateTo,
      }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          size: size.toString(),
        })

        const searchByName = contractorOrganizationName?.trim()
        if (searchByName && searchByName.length > 0) {
          params.set('contractorOrganizationName', searchByName)
        }

        const searchByLotName = lotName?.trim()
        if (searchByLotName && searchByLotName.length > 0) {
          params.set('lotName', searchByLotName)
        }
        if (createdDateFrom) {
          params.set('createdDateFrom', createdDateFrom)
        }
        if (createdDateTo) {
          params.set('createdDateTo', createdDateTo)
        }

        return {
          url: '/contracts/public',
          method: 'get',
          params,
        }
      },
      providesTags: ['ContractsPublic'],
    }),
    contract: builder.query<IContractItem, IdParams>({
      query: ({ id }) => `/contracts/${id}`,
      providesTags: ['Contract'],
    }),
    createContract: builder.mutation<void, Partial<IContractItem>>({
      query: (body) => ({
        url: `/contracts`,
        body,
        method: 'post',
      }),
      invalidatesTags: ['Contracts'],
    }),
    publishContract: builder.mutation<IContractItem, IdParams>({
      query: ({ id }) => ({
        url: `/contracts/${id}/publish`,
        method: 'put',
      }),
      invalidatesTags: ['Contracts', 'Contract'],
    }),
    updateContract: builder.mutation<void, Partial<IContractItem>>({
      query: (body) => ({
        url: `/contracts/${body.id}`,
        body,
        method: 'put',
      }),
      invalidatesTags: ['Contract', 'Contracts'],
    }),
  }),
})

export const {
  useContractQuery,
  useContractsQuery,
  useContractsPublicQuery,
  useCreateContractMutation,
  usePublishContractMutation,
  useUpdateContractMutation,
} = contractsApi
